import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { Box, Stack, Typography } from '@mui/material'
import { availableToColor, availableToBgColor, availableToTranslationKey, isRackType } from '../../../helpers'
import Pictures, { OPERATION_PICTURES } from '../../Infras/CompleteDetailView/modales/Pictures'

const PopupContent = ({ marker }) => {
  const { t } = useTranslation()
  const [picturesOpen, setPicturesOpen] = useState(false)
  const { name, device } = marker
  const isRack = isRackType(device?.model?.type)
  const uuid = device?.uuid

  const { data: picturesData } = useQuery(OPERATION_PICTURES, {
    variables: { uuid },
    skip: !uuid
  })
  const firstPicture = picturesData?.pictures?.[0]
  const picturesCount = picturesData?.pictures?.length ?? 0

  const handleOpenPictures = () => setPicturesOpen(true)
  const handleClosePictures = () => setPicturesOpen(false)

  const status = useMemo(() => {
    if (isRack) {
      return {
        label: t(availableToTranslationKey('installed')),
        color: availableToColor('installed'),
        bgcolor: availableToBgColor('installed')
      }
    }

    let trad = ['hibernation', 'works'].includes(device.available_level)
      ? `${device.available_level}_${device.is_reachable ? 'up' : 'down'}`
      : device.available_level

    return {
      label: t(availableToTranslationKey(trad)),
      color: availableToColor(trad),
      bgcolor: availableToBgColor(trad)
    }
  }, [device, isRack, t])

  const deviceTypeLabel = t(`cartography.maps.legend.${device?.model?.type}`)

  const infoRows = [
    { key: t('supervision.popup.manufacturer'), value: device?.model?.manufacturer || '-' },
    { key: t('supervision.popup.model'), value: device?.model?.name || '-' },
    ...(!isRack
      ? [
          { key: t('supervision.popup.mac_address'), value: device?.mac || '-' },
          { key: t('supervision.popup.ip_address'), value: device?.ip_address || 'N/A' }
        ]
      : [])
  ]

  const isAp = device?.model?.type === 'ap'
  const ssids = device?.ssid_names ?? []

  return (
    <Stack sx={{ minWidth: 280, maxWidth: 320 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ pt: 0, pb: 1.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '24px',
            border: '1px solid #EFEFEF',
            borderRadius: '24px',
            px: 1.5
          }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 500,
              lineHeight: '16px',
              fontFamily: 'Poppins',
              color: 'content.primary'
            }}>
            {deviceTypeLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '24px',
            backgroundColor: status.bgcolor,
            borderRadius: '24px',
            px: 1.5
          }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 500,
              lineHeight: '16px',
              fontFamily: 'Poppins',
              color: status.color,
              textTransform: 'capitalize'
            }}>
            {status.label}
          </Typography>
        </Box>
      </Stack>

      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '16px',
          fontFamily: 'Poppins',
          color: '#2570EB',
          py: 1
        }}>
        {name}
      </Typography>

      <Stack>
        {infoRows.map(({ key, value }) => (
          <Stack
            key={key}
            direction="row"
            sx={{
              py: 0.5,
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
            <Typography
              sx={{
                fontSize: 12,
                color: '#464646',
                fontWeight: 500,
                fontFamily: 'Poppins',
                textTransform: 'capitalize'
              }}>
              {key}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F7F7F7', mx: 2 }} />
            <Typography
              sx={{
                fontSize: 12,
                color: '#656565',
                fontWeight: 400,
                textAlign: 'right',
                maxWidth: '160px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              {value}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {isAp && (
        <Stack sx={{ pb: 1, pt: 0.5 }}>
          <Stack direction="row" alignItems="center" sx={{ py: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: '#464646',
                fontWeight: 500,
                fontFamily: 'Poppins',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap'
              }}>
              {t('supervision.popup.ssids')}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F7F7F7', ml: 2 }} />
          </Stack>
          <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 400, fontFamily: 'Poppins' }}>
            {ssids.length > 0 ? ssids.join(', ') : '-'}
          </Typography>
        </Stack>
      )}

      {firstPicture && (
        <Box
          component="button"
          type="button"
          aria-label={t('supervision.hardware.detail.photos')}
          onClick={handleOpenPictures}
          sx={{
            position: 'relative',
            width: '100%',
            height: 140,
            mt: 1,
            mb: 0.5,
            p: 0,
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#F6F7F8',
            cursor: 'zoom-in',
            '&:hover, &:focus-visible': {
              borderColor: '#2570EB'
            },
            '&:hover .picture-overlay, &:focus-visible .picture-overlay': {
              opacity: 1
            }
          }}>
          <Box
            component="img"
            src={firstPicture.url}
            alt={t('supervision.hardware.detail.photos')}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
          <Box
            className="picture-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.35)',
              color: '#FFFFFF',
              opacity: 0,
              transition: 'opacity 150ms ease-in-out'
            }}>
            <Box component="i" className="fa-light fa-expand" sx={{ fontSize: 20 }} />
          </Box>
          {picturesCount > 1 && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                px: 1,
                height: 20,
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#FFFFFF'
              }}>
              <Box component="i" className="fa-light fa-images" sx={{ fontSize: 10 }} />
              <Typography sx={{ fontSize: 10, fontWeight: 500, lineHeight: '16px', fontFamily: 'Poppins' }}>
                {picturesCount}
              </Typography>
            </Stack>
          )}
        </Box>
      )}

      {!firstPicture && !isAp && <Box sx={{ pb: 1 }} />}

      <Pictures open={picturesOpen} onClose={handleClosePictures} uuid={uuid} />
    </Stack>
  )
}

export default PopupContent
